import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { useParams, useLocation } from 'react-router-dom'
import { WebPlayerView } from 'unified-video-framework/web'
import MessageBox from '../../components/CommonComponents/MessageBox.jsx'
import Defaultloadinggif from '../../layout/Others/Defaultloadinggif.js'
import './UnifiedVideoPlayer.css'

const UnifiedVideoPlayer = () => {
    const { id, slug } = useParams();

    const baseURL = process.env.REACT_APP_Baseurl

    const location = useLocation();
    const pathname = location.pathname;

    const isPlayout = pathname.includes('playout')
    const video_slug = isPlayout ? slug : `${id}/${slug}`

    const [videoData, setVideoData] = useState({
        state: "loading",
        error: null,
    })

    const [loading, setLoading] = useState(true)

    // Helper: convert "hh:mm:ss" → seconds
    const timeToSeconds = (timeString) => {
        if (!timeString) return null
        const parts = timeString.split(":").map(Number)
        if (parts.length === 3) {
            const [hh, mm, ss] = parts
            return hh * 3600 + mm * 60 + ss
        } else if (parts.length === 2) {
            const [mm, ss] = parts
            return mm * 60 + ss
        } else if (parts.length === 1) {
            return parts[0]
        }
        return null
    }

    // Handle popup redirect from payment gateway
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const paymentStatus = urlParams.get('payment')
        const isPopup = urlParams.get('popup')
        const gateway = urlParams.get('gateway')
        const orderId = urlParams.get('order_id')
        if (paymentStatus && isPopup) {
            console.log(`🔔 Payment ${paymentStatus} in popup`, { gateway, orderId })
            if (window.opener) {
                window.opener.postMessage({
                    type: 'uvfCheckout',
                    status: paymentStatus,
                    orderId: orderId,
                    gatewayId: gateway
                }, '*')
                window.close()
            }
        }
    }, [])

    // Fetch video details
    useEffect(() => {
        if (video_slug) {
            const fetchVideoBySlug = async () => {
                try {
                    setLoading(true)
                    const uvfToken = sessionStorage.getItem("uvf_session_token") || localStorage.getItem("uvf_session_token")
                    const userEmail = localStorage.getItem("uvf_user_id") || null;

                    const response = await axios.get(`${baseURL}/api/video/${video_slug}`, {
                        headers: {
                            "Content-Type": "application/json",
                            Accept: "application/json",
                            ...(uvfToken ? { "x-session-token": uvfToken } : {}),
                        },
                        params: {
                            email: userEmail,
                            isPlayout
                        }
                    })

                    if (response?.data?.status) {
                        setVideoData(response.data.data)
                    } else {
                        setVideoData((prev) => ({
                            ...prev,
                            error: response.data?.message || "Video not found or not available"
                        }))
                    }
                } catch (error) {
                    console.error('Error fetching video:', error)
                    setVideoData((prev) => ({
                        ...prev,
                        error: error?.response?.data?.message || error?.message || "Failed to load video"
                    }))
                } finally {
                    setLoading(false)
                }
            }

            fetchVideoBySlug()
        }
    }, [video_slug, baseURL])

    if (loading) {
        return (
            <div className="player-loading-container"
                style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Defaultloadinggif />
            </div>
        )
    }

    if (videoData?.error) {
        return (
            <div className="player-error-container"
                style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '20px' }}>
                <MessageBox
                    text={`Error: ${videoData?.error}`}
                    classname="warningColor text-center my-5 d-block"
                />
            </div>
        )
    }

    const access_token = localStorage.getItem("access_token")
    const session_token = sessionStorage.getItem("uvf_session_token")
    const effectiveToken = session_token || access_token

    const getPaywallConfig = () => {
        return {
            enabled: true,
            apiBase: baseURL,
            userId: null,
            userEmail: sessionStorage.getItem("uvf_user_id") || null,
            videoId: videoData?.id,
            videoSlug: !isPlayout ? video_slug : slug,
            metadata: { slug: !isPlayout ? video_slug : slug, isPlayout: isPlayout },
            pricing: {
                amount: videoData?.price,
                currency: 'INR',
                description: `Watch "${videoData?.title || ''}"`,
            },
            gateways: [
                { id: 'cashfree', name: 'Cashfree', color: '#00d4aa' },
                // { id: 'payu', name: 'PayU', color: '#17bf43' }
            ],
            paymentLink: {
                endpoint: `${baseURL}/Front-End/cashfree/ppv-payment`,
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${access_token}`
                },
                mapRequest: (paymentData) => {
                    const currentUrl = window.location.origin + window.location.pathname
                    const amount = Math.round(paymentData.amount || videoData?.price || 100)
                    const userEmail = localStorage.getItem("uvf_user_id") || null;
                    return {
                        unit_amount: amount,
                        source_type_id: 1,
                        source_id: videoData?.id,
                        email: userEmail,
                        success_url: `${currentUrl}?payment=success&popup=1&gateway=${paymentData.metadata?.gateway}`,
                        failure_url: `${currentUrl}?payment=failed&popup=1&gateway=${paymentData.metadata?.gateway}`
                    }
                },
            },
            ui: {
                theme: 'dark',
                modal: {
                    backdrop: true,
                    closeOnBackdrop: false,
                    showCloseButton: true,
                    zIndex: 10000,
                    overlay: { backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(4px)' }
                },
            },
            branding: {
                title: 'Unlock Premium Content',
                description: `Pay ₹${videoData?.price || '100'} to watch "${videoData?.title || ''}" instantly`,
                brandColor: '#ff6b35',
                paymentTitle: 'Choose your preferred payment method'
            },
            emailAuth: {
                enabled: true,
                skipIfAuthenticated: true,
                api: {
                    requestOtp: `/api/unified-video/send-email-otp`,
                    verifyOtp: `/api/unified-video/verify-email-otp`,
                },
                sessionStorage: {
                    tokenKey: 'uvf_session_token',
                    userIdKey: 'uvf_user_id',
                    emailKey: 'uvf_user_email',
                },
                requestPayload: { video_slug: !isPlayout ? video_slug : slug },
                ui: {
                    title: "Sign in to continue watching",
                    description: `Enter your email to receive a verification code and unlock "${videoData?.title || 'this video'}"`,
                    emailPlaceholder: "Enter your email address",
                    otpPlaceholder: "Enter 6-digit verification code",
                    submitButtonText: "Send Verification Code",
                    resendButtonText: "Resend Code",
                    resendCooldown: 30,
                    verifyButtonText: "Verify & Watch",
                    brandColor: "#ff6b35",
                },
                validation: {
                    otpLength: 6,
                    otpTimeout: 600,
                    rateLimiting: { maxAttempts: 5, windowMinutes: 60 },
                },
            }
        }
    }

    return (
        <>
            <style>{`
        .uvf-paywall-overlay {
          position: fixed !important; top: 0 !important; left: 0 !important;
          width: 100vw !important; height: 100vh !important; z-index: 99999 !important;
          background: rgba(0, 0, 0, 0.8) !important; display: flex !important;
          align-items: center !important; justify-content: center !important;
        }
        .uvf-paywall-modal {
          background: #1a1a1a !important; border-radius: 12px !important;
          padding: 24px !important; max-width: 500px !important; width: 90% !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important; color: white !important;
        }
        .uvf-payment-button {
          background: #ff6b35 !important; color: white !important;
          border: none !important; padding: 12px 24px !important; border-radius: 8px !important;
          font-size: 16px !important; cursor: pointer !important; width: 100% !important;
          margin-top: 16px !important; transition: background 0.2s ease !important;
        }
        .uvf-payment-button:hover { background: #ff8c42 !important; }
      `}</style>

            <div className="video-player-page"
                style={{ height: '100vh', maxHeight: '100vh', backgroundColor: '#000', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <WebPlayerView
                    url={videoData?.video_url || videoData?.substitute_url}
                    fallbackSources={[{ url: videoData?.substitute_url }, { url: videoData?.video_url }]}
                    fallbackShowErrorMessage={true}
                    fallbackPoster='false'
                    freeDuration={timeToSeconds(videoData?.free_duration)}
                    responsive={{ enabled: true, aspectRatio: 16 / 9, maxWidth: '100vw', maxHeight: '100vh' }}
                    style={{ width: '100%', height: '100%', maxWidth: '100vw', maxHeight: '100vh', borderRadius: 0 }}
                    paywall={getPaywallConfig()}
                    playerTheme={{
                        accent: '#ff6b35', accent2: '#ff8c42',
                        iconColor: '#ffffff', textPrimary: '#ffffff',
                        textSecondary: '#cccccc', overlayStrong: 'rgba(0,0,0,0.95)',
                    }}
                    metadata={{
                        title: `${videoData?.title || videoData?.name}`,
                    }}
                    settings={{ subtitles: false, speed: true, quality: true }}
                    autoPlay={true}
                    muted={false}
                    enableAdaptiveBitrate={true}
                    debug={false}
                    watermark={false}
                    loop={true}
                    showFrameworkBranding={false}
                    onReady={(player) => { window.player = player }}
                    onError={(error) => { console.error('❌ Player error:', error) }}
                    onVideoStart={() => { console.log('▶️ Video playback started') }}
                    onVideoEnd={() => { console.log('⏹️ Video playback ended') }}
                    onPlay={() => { console.log('▶️ Video play event') }}
                    onPause={() => { console.log('⏸️ Video pause event') }}
                    onFreePreviewEnded={() => {
                        console.log('⏰ Free preview ended - email auth + paywall should trigger')
                    }}
                    // Email auth handlers
                    onAuthStart={(email) => { console.log('🔐 Email auth started for:', email) }}
                    onOtpSent={(response) => { console.log('📧 OTP sent successfully:', response) }}
                    onAuthSuccess={(userData) => {
                        if (userData) {
                            sessionStorage.setItem('uvf_user_id', userData.id || userData.user_id)
                            sessionStorage.setItem('uvf_user_email', userData.email)
                            if (userData.token) {
                                sessionStorage.setItem('uvf_session_token', userData.token)
                            }
                        }
                    }}
                    onAuthError={(error) => { console.error('❌ Email auth error:', error) }}
                    // Payment handlers
                    onPaymentStart={(gateway, paymentData) => {
                        console.log('💳 Payment started:', { gateway, paymentData })
                    }}
                    onPaymentSuccess={async (gateway, paymentResult) => {
                        console.log('💰 Payment successful:', { gateway, paymentResult })
                        try {
                            const token = sessionStorage.getItem("uvf_session_token") || localStorage.getItem("access_token")

                            // ✅ Call backend to store payment details
                            await axios.post(`${baseURL}/api/unified-video/store-payment`, {
                                session_token: token,
                                video_id: videoData?.id,
                                gateway: gateway,
                                order_id: paymentResult?.orderId || paymentResult?.id,
                                amount: videoData?.price,
                                currency: "INR"
                            }, {
                                headers: {
                                    "Content-Type": "application/json",
                                    ...(token ? { "x-session-token": token } : {})
                                }
                            })

                            // ✅ Refresh entitlement
                            const resp = await axios.get(`${baseURL}/api/video/${video_slug}`, {
                                headers: {
                                    "Content-Type": "application/json",
                                    Accept: "application/json",
                                    ...(token ? { "x-session-token": token } : {})
                                }
                            })
                            if (resp?.data?.status) {
                                setVideoData(resp.data.data)
                                console.log("✅ Video entitlement refreshed after payment")
                            }
                        } catch (err) {
                            console.error("❌ Failed to update entitlement:", err)
                        }
                    }}
                    onPaymentError={(gateway, error) => {
                        console.error('❌ Payment failed:', { gateway, error })
                    }}
                    onPaymentCancel={(gateway) => {
                        console.log('🚫 Payment cancelled by user:', gateway)
                    }}
                />
            </div>
        </>
    )
}

export default UnifiedVideoPlayer
